/**
 * XState 5 dev tools integration:
 * A) Lightweight actor registry — always active in dev, provides getActiveActorStates()
 * B) Visual inspector — opt-in via ?inspect URL param
 */

const STORAGE_KEY = 'wefa_inspect'

interface ActorSnapshot {
  machineId: string
  state: string
  context: Record<string, unknown>
}

const activeActors = new Map<string, ActorSnapshot>()

/**
 * Returns current state of all active XState actors.
 * In prod or when registry is not initialized, returns empty object.
 */
export function getActiveActorStates(): Record<string, ActorSnapshot> {
  return Object.fromEntries(activeActors)
}

function isInspectEnabled(): boolean {
  const params = new URLSearchParams(window.location.search)
  if (params.has('inspect')) {
    const val = params.get('inspect')
    const enabled = !(val === 'off' || val === '0' || val === 'false')
    if (enabled) sessionStorage.setItem(STORAGE_KEY, '1')
    else sessionStorage.removeItem(STORAGE_KEY)
    return enabled
  }
  return sessionStorage.getItem(STORAGE_KEY) === '1'
}

export function enableInspector(): void {
  sessionStorage.setItem(STORAGE_KEY, '1')
  window.location.reload()
}

export function disableInspector(): void {
  sessionStorage.removeItem(STORAGE_KEY)
  window.location.reload()
}

export function isInspectorActive(): boolean {
  if (!import.meta.env.DEV) return false
  return sessionStorage.getItem(STORAGE_KEY) === '1'
}

/**
 * Installs a global inspection handler via XState 5's dev tools protocol.
 * XState 5 actors send inspection events (@xstate.actor, @xstate.snapshot)
 * to the `inspect` function on `globalThis.__xstate__`.
 */
export function initActorRegistry(): void {
  if (!import.meta.env.DEV) return

  const inspectHandler = (event: unknown) => {
    const evt = event as {
      type: string
      actorRef?: { sessionId: string; id: string }
      snapshot?: { value?: unknown; context?: unknown; status?: string }
    }

    if (evt.type === '@xstate.actor' && evt.actorRef) {
      activeActors.set(evt.actorRef.sessionId, {
        machineId: evt.actorRef.id,
        state: '',
        context: {},
      })
    }

    if (evt.type === '@xstate.snapshot' && evt.actorRef && evt.snapshot) {
      if (evt.snapshot.status === 'stopped') {
        activeActors.delete(evt.actorRef.sessionId)
      } else {
        activeActors.set(evt.actorRef.sessionId, {
          machineId: evt.actorRef.id,
          state: String(evt.snapshot.value ?? ''),
          context: (evt.snapshot.context ?? {}) as Record<string, unknown>,
        })
      }
    }
  }

  // Wrap any existing inspect handler (e.g. from @statelyai/inspect)
  const existing = (globalThis as Record<string, unknown>).__xstate__ as
    | { inspect?: (event: unknown) => void }
    | undefined

  if (existing?.inspect) {
    const original = existing.inspect
    existing.inspect = (event: unknown) => {
      original(event)
      inspectHandler(event)
    }
  } else {
    ;(globalThis as Record<string, unknown>).__xstate__ = {
      ...existing,
      inspect: inspectHandler,
    }
  }
}

/**
 * Conditionally loads the Stately visual inspector when ?inspect is active.
 */
export async function initXStateInspector(): Promise<void> {
  if (!import.meta.env.DEV) return
  if (!isInspectEnabled()) return

  try {
    const { createBrowserInspector } = await import('@statelyai/inspect')
    createBrowserInspector()
  } catch (error) {
    console.warn('[WEFA:xstate] Failed to load inspector:', error)
  }
}
